import React, { useState, useEffect } from 'react'
import { QrCode, Copy, CheckCircle, Clock, RefreshCw } from 'lucide-react'
import { logger } from '../../utils/logger'

interface PixFormProps {
  qrCode?: string
  pixCode: string
  amount: number
  currency: string
  onPaymentSuccess?: () => void
  onPaymentFailure?: () => void
}

const PixForm: React.FC<PixFormProps> = ({ 
  pixCode, 
  amount, 
  currency
}) => {
  const [copied, setCopied] = useState(false)
  const [timeLeft, setTimeLeft] = useState(15 * 60) // 15 minutos em segundos
  const [isExpired, setIsExpired] = useState(false)

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setIsExpired(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(pixCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      logger.error('Erro ao copiar PIX:', error)
    }
  }

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency
    }).format(amount)
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center mb-6">
        <QrCode className="w-6 h-6 text-green-600 mr-3" />
        <h3 className="text-lg font-semibold text-gray-900">Pagamento via PIX</h3>
      </div>

      {isExpired ? (
        <div className="text-center py-8">
          <Clock className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-gray-900 mb-2">PIX Expirado</h4>
          <p className="text-gray-600 mb-4">
            O código PIX expirou. Gere um novo código para continuar o pagamento.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center mx-auto"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Gerar Novo PIX
          </button>
        </div>
      ) : (
        <>
          {/* QR Code */}
          <div className="text-center mb-6">
            <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block mb-4">
              <QrCode className="w-32 h-32 text-gray-400" />
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Escaneie o QR Code com seu app de pagamento
            </p>
          </div>

          {/* Código PIX */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Código PIX (Copiar e Colar)
            </label>
            <div className="relative">
              <textarea
                value={pixCode}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono resize-none"
                rows={4}
              />
              <button
                onClick={copyToClipboard}
                className="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {copied ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
            {copied && (
              <p className="mt-1 text-sm text-green-600">Código copiado!</p>
            )}
          </div>

          {/* Informações do Pagamento */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-600">Valor:</span>
              <span className="text-lg font-semibold text-gray-900">
                {formatCurrency(amount, currency)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Tempo restante:</span>
              <span className={`font-semibold ${timeLeft < 300 ? 'text-red-600' : 'text-gray-900'}`}>
                {formatTime(timeLeft)}
              </span>
            </div>
          </div>

          {/* Instruções */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Como pagar:</h4>
            <ol className="text-sm text-blue-800 space-y-1">
              <li>1. Abra seu app de pagamento (banco, carteira digital, etc.)</li>
              <li>2. Escolha a opção "PIX"</li>
              <li>3. Escaneie o QR Code ou cole o código PIX</li>
              <li>4. Confirme o pagamento</li>
            </ol>
          </div>

          {/* Status do Pagamento */}
          <div className="mt-6 text-center">
            <div className="flex items-center justify-center text-gray-600">
              <Clock className="w-4 h-4 mr-2" />
              <span className="text-sm">
                Aguardando confirmação do pagamento...
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default PixForm
